import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getOhlcv } from '../services/marketdata';
import useMarketStream from '../hooks/useMarketStream';

const UP   = '#26a69a';
const DOWN = '#ef5350';

// Chart palette per theme. Mirrors the --pt-* tokens in PaperTrading.css so the
// canvas (drawn by lightweight-charts via JS, not CSS) follows the light/dark
// toggle alongside the rest of the terminal.
function chartPalette(theme) {
  if (theme === 'light') {
    return {
      bg: '#ffffff', grid: '#e8edf5', text: '#586577',
      crosshair: '#97a3b4', crosshairLabelBg: '#dde3ee',
      priceLine: '#97a3b4',
      up: '#0a8f7f', down: '#e5484d',
      intervalBorder: '#c8d0e0', intervalText: '#586577', onAccent: '#ffffff',
      bright: '#0a0f1e',
    };
  }
  return {
    bg: '#0a0b0d', grid: '#1e2028', text: '#9ca3af',
    crosshair: '#4b5563', crosshairLabelBg: '#1e2028',
    priceLine: '#4b5563',
    up: UP, down: DOWN,
    intervalBorder: '#2a2d36', intervalText: '#9ca3af', onAccent: '#0a0b0d',
    bright: '#f1f5f9',
  };
}

// Selectable candle intervals and the recent window fetched for each. Yahoo caps
// intraday lookback (1m ≈ 7d, 5m/15m/30m ≈ 60d, 1h ≈ 730d), so these stay
// within what's served.
const INTERVALS = ['1m', '5m', '15m', '30m', '1h', '1d'];
const PERIOD_FOR_INTERVAL = {
  '1m': '1d', '5m': '5d', '15m': '1mo',
  '30m': '1mo', '1h': '3mo', '1d': '1y',
};
const DEFAULT_INTERVAL = '1m';
const INTERVAL_PARAM = 'interval';

// Interval length in seconds, used to align live ticks onto the correct bar.
const INTERVAL_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800, '1h': 3600, '1d': 86400,
};

// Safety-net refetch cadence. Deliberately slow: the tick stream does the
// second-to-second work, so this only repairs drift.
const REFRESH_MS = 15 * 60 * 1000;
// IST offset, so intraday bars bucket on the exchange's clock (matching the
// server, which aligns bars to the IST calendar).
const IST_OFFSET_SEC = 5.5 * 3600;

function fmt(n, d = 2) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
}


function toUnix(ts) {
  return Math.floor(new Date(ts).getTime() / 1000);
}

// ── IST display helpers ─────────────────────────────────────────────────────
// lightweight-charts renders all timestamps in UTC (no native timezone
// support), so both the axis ticks and the crosshair label must be converted
// to IST (Asia/Kolkata) explicitly. `ts` is a UTC unix timestamp in seconds.
function istHourMin(ts) {
  return new Date(ts * 1000).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour12: false, hour: '2-digit', minute: '2-digit',
  });
}
function istDayMon(ts) {
  return new Date(ts * 1000).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
  });
}

/**
 * StockChart — candlestick chart driven by stored OHLCV candles.
 *
 * Candles come pre-built from the market-data provider (Yahoo) at
 * 1m / 5m / 15m / 30m / 1h / 1d, fetched from QuestDB stock_ohlcv via
 * GET /marketdata/history/:symbol. We never
 * aggregate candles client-side — the bars match what real platforms show.
 *
 * Update model
 * -------------
 * History is fetched ONCE per (symbol, interval) and then kept current from the
 * live tick stream, which updates the last bar in place and rolls a new one at
 * each interval boundary.
 *
 * It previously re-fetched on every change of the `quote` prop. That was
 * tolerable when bars came from QuestDB (a cheap local query), but the history
 * endpoint can now be served by a rate-limited upstream provider, and the tick
 * stream updates ~4x/second — so a quote-driven refetch would mean several
 * upstream calls per second per open chart. Hence the two paths are decoupled:
 *   • live price  -> series.update() on the last bar only (no network)
 *   • correctness -> a slow periodic refetch, plus on reconnect/refocus
 *
 * REFRESH_MS is a safety net, not the update mechanism: socket-built bars can
 * drift if ticks are missed, so the authoritative bars are re-pulled
 * occasionally.
 *
 * Props:
 *   symbol {string}   e.g. "HDFCBANK"
 *   quote  {object}   { ltp, change, change_pct, prev_close, ts }
 *   token  {string}   auth token for the history API call
 */
export default function StockChart({ symbol, quote, token, theme }) {
  const pal = chartPalette(theme);
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  // Mirror of the newest candle, so a live tick can extend it without re-reading
  // from the series (lightweight-charts has no getter for the last bar).
  const lastBarRef   = useRef(null);
  const hasDataRef   = useRef(false);
  const roRef        = useRef(null);

  // Candle interval is mirrored in the URL (?interval=) so a full refresh and
  // shared links restore the selection — same pattern the parent uses for ?symbol=.
  const [searchParams, setSearchParams] = useSearchParams();
  const [chartInterval, setChartInterval] = useState(() => {
    const fromUrl = searchParams.get(INTERVAL_PARAM);
    return INTERVALS.includes(fromUrl) ? fromUrl : DEFAULT_INTERVAL;
  });
  const [loading,      setLoading]      = useState(false);
  const [hasData,      setHasData]      = useState(false);

  // Persist the interval to the URL whenever it changes (merge, replace —
  // preserves ?symbol= set by the parent).
  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(INTERVAL_PARAM, chartInterval);
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInterval]);

  // ── Create chart once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const p = chartPalette(theme);
    const chart = createChart(containerRef.current, {
      layout: {
        background:  { color: p.bg },
        textColor:   p.text,
        fontFamily:  "'Inter','Roboto',sans-serif",
        fontSize:    11,
      },
      grid: {
        vertLines: { color: p.grid },
        horzLines: { color: p.grid },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: p.crosshair, labelBackgroundColor: p.crosshairLabelBg, width: 1, style: 3 },
        horzLine: { color: p.crosshair, labelBackgroundColor: p.crosshairLabelBg, width: 1, style: 3 },
      },
      rightPriceScale: {
        borderColor:  p.grid,
        minimumWidth: 70,
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor:    p.grid,
        timeVisible:    true,
        secondsVisible: false,
        rightOffset:    5,
        // Axis tick labels in IST. tickMarkType >= 3 (Time/TimeWithSeconds)
        // shows the clock; 0–2 (Year/Month/DayOfMonth) shows the date.
        tickMarkFormatter: (ts, tickMarkType) =>
          tickMarkType >= 3 ? istHourMin(ts) : istDayMon(ts),
      },
      localization: {
        // Crosshair time label in IST.
        timeFormatter: (ts) => `${istDayMon(ts)} ${istHourMin(ts)}`,
      },
      handleScroll: true,
      handleScale:  true,
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor:         p.up,
      downColor:       p.down,
      borderUpColor:   p.up,
      borderDownColor: p.down,
      wickUpColor:     p.up,
      wickDownColor:   p.down,
      priceLineVisible: true,
      priceLineColor:   p.priceLine,
      lastValueVisible: true,
    });

    chartRef.current  = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(entries => {
      const e = entries[0];
      if (e && chartRef.current) {
        chartRef.current.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
      }
    });
    ro.observe(containerRef.current);
    roRef.current = ro;

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
    };
  // Created once — re-coloured live by the theme effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-colour the chart when the theme toggles ──────────────────────────────
  useEffect(() => {
    if (!chartRef.current || !seriesRef.current) return;
    const p = chartPalette(theme);
    chartRef.current.applyOptions({
      layout: { background: { color: p.bg }, textColor: p.text },
      grid: { vertLines: { color: p.grid }, horzLines: { color: p.grid } },
      crosshair: {
        vertLine: { color: p.crosshair, labelBackgroundColor: p.crosshairLabelBg },
        horzLine: { color: p.crosshair, labelBackgroundColor: p.crosshairLabelBg },
      },
      rightPriceScale: { borderColor: p.grid },
      timeScale: { borderColor: p.grid },
    });
    seriesRef.current.applyOptions({
      upColor: p.up, downColor: p.down,
      borderUpColor: p.up, borderDownColor: p.down,
      wickUpColor: p.up, wickDownColor: p.down,
      priceLineColor: p.priceLine,
    });
  }, [theme]);

  // ── Fetch candles from QuestDB ──────────────────────────────────────────────
  // silent=true → background refresh (no spinner, no re-fit): keeps the latest
  // candle fresh on each poll. silent=false → full (re)load on symbol/interval.
  const fetchBars = useCallback(async (silent) => {
    if (!symbol || !token || !seriesRef.current) return;

    if (!silent) {
      setLoading(true);
      setHasData(false);
      hasDataRef.current = false;
      seriesRef.current.setData([]);
      lastBarRef.current = null;
    }

    try {
      const period = PERIOD_FOR_INTERVAL[chartInterval] || '5d';
      const res = await getOhlcv(token, symbol, period, chartInterval);
      if (!res.data?.success) return;

      const bars = res.data.data?.bars || [];

      // Map to candlestick points, deduped & ascending by unix-second time.
      const seen = new Set();
      const data = [];
      for (const b of bars) {
        const t = toUnix(b.ts);
        if (!seen.has(t)) {
          seen.add(t);
          data.push({
            time:  t,
            open:  parseFloat(b.open),
            high:  parseFloat(b.high),
            low:   parseFloat(b.low),
            close: parseFloat(b.close),
          });
        }
      }

      if (data.length === 0) return;

      seriesRef.current.setData(data);
      // Re-seed the live-update anchor from authoritative data, so the next tick
      // extends the real bar rather than one built from a partial view.
      lastBarRef.current = data[data.length - 1];
      setHasData(true);
      hasDataRef.current = true;
      if (!silent) {
        // Re-enable price-axis auto-scaling so the vertical range resets to the
        // new symbol's prices (a previous manual vertical zoom would otherwise
        // stick, e.g. a ₹1000 stock's range left over when switching to a ₹200
        // one). fitContent() only fits the time axis, not the price scale.
        chartRef.current?.priceScale('right').applyOptions({ autoScale: true });
        chartRef.current?.timeScale().fitContent();
      }
    } catch { /* silent — overlay shows the empty state */ }
    finally { if (!silent) setLoading(false); }
  }, [symbol, token, chartInterval]);

  // Full load whenever symbol or interval changes.
  useEffect(() => { fetchBars(false); }, [fetchBars]);

  // Safety-net refetch. Slow on purpose — the tick stream keeps the chart
  // current; this only repairs bars that drifted from missed ticks.
  useEffect(() => {
    if (!symbol || !token) return undefined;
    const id = setInterval(() => {
      if (hasDataRef.current) fetchBars(true);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [symbol, token, fetchBars]);

  // Re-pull authoritative bars when the tab regains focus: a backgrounded tab
  // is throttled by the browser and will have missed ticks.
  useEffect(() => {
    if (!symbol || !token) return undefined;
    const onFocus = () => {
      if (document.visibilityState === 'visible' && hasDataRef.current) fetchBars(true);
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [symbol, token, fetchBars]);

  // ── Live tick -> last bar, in place ────────────────────────────────────────
  const { ticks, isLive } = useMarketStream(token, symbol ? [symbol] : []);
  const liveTick = symbol ? ticks[symbol] : null;

  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !liveTick || !hasDataRef.current) return;

    const price = Number(liveTick.ltp);
    if (!Number.isFinite(price) || price <= 0) return;

    const intervalSec = INTERVAL_SECONDS[chartInterval] || 60;
    const nowSec = Math.floor((liveTick.ts || Date.now()) / 1000);
    // Bucket on the IST clock so a live-built bar lands on the same boundary as
    // a fetched one. Flooring in UTC would misalign 30m/1h/1d.
    const barTime = Math.floor((nowSec + IST_OFFSET_SEC) / intervalSec) * intervalSec - IST_OFFSET_SEC;

    const last = lastBarRef.current;

    if (last && barTime === last.time) {
      // Extend the forming bar.
      const updated = {
        time: last.time,
        open: last.open,
        high: Math.max(last.high, price),
        low: Math.min(last.low, price),
        close: price,
      };
      lastBarRef.current = updated;
      series.update(updated);
      return;
    }

    // Guard against going backwards: lightweight-charts throws on a
    // non-monotonic time, which would break the chart until remount.
    if (last && barTime < last.time) return;

    const fresh = { time: barTime, open: price, high: price, low: price, close: price };
    lastBarRef.current = fresh;
    series.update(fresh);
  }, [liveTick, chartInterval]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // Prefer the streamed price so the header matches the moving candle instead of
  // lagging the parent's 30s poll. Change/%-change still come from the quote:
  // they need prev_close, which the lite tick stream does not carry.
  const polledLtp = parseFloat(quote?.ltp);
  const streamLtp = liveTick ? Number(liveTick.ltp) : null;
  const ltp       = Number.isFinite(streamLtp) ? streamLtp
                  : (Number.isFinite(polledLtp) ? polledLtp : null);
  const change    = parseFloat(quote?.change)     || 0;
  const changePct = parseFloat(quote?.change_pct) || 0;
  const isUp      = change >= 0;

  return (
    <div className="sc-wrap">
      <div className="sc-header">
        {/* Symbol, exchange, price and change all on one row; the interval
            buttons sit at the far right of the same row (sc-header pushes them
            there with space-between). Previously sc-header-left was a column,
            so the price wrapped under the symbol and made the header two lines
            tall for no benefit. */}
        <div className="sc-header-left">
          <span className="sc-symbol">{symbol || '—'}</span>
          <span className="sc-exchange">NSE</span>
          {ltp != null && (
            <span
              className={`sc-live-dot${isLive ? '' : ' sc-live-dot-delayed'}`}
              title={isLive ? 'Streaming live' : 'Delayed — polling'}
            />
          )}
          {ltp != null && (
            <>
              <span className="sc-ltp">₹{fmt(ltp)}</span>
              <span className={`sc-chg ${isUp ? 'up' : 'down'}`}>
                {isUp ? '▲' : '▼'} {fmt(Math.abs(change))} ({fmt(Math.abs(changePct))}%)
              </span>
            </>
          )}
        </div>

        <div className="sc-interval-toggle" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {INTERVALS.map(iv => (
            <button
              key={iv}
              type="button"
              onClick={() => setChartInterval(iv)}
              style={{
                padding: '3px 9px',
                fontSize: 11,
                lineHeight: 1.2,
                borderRadius: 4,
                cursor: 'pointer',
                border: `1px solid ${pal.intervalBorder}`,
                background: chartInterval === iv ? pal.up : 'transparent',
                color: chartInterval === iv ? pal.onAccent : pal.intervalText,
                fontWeight: chartInterval === iv ? 600 : 400,
              }}
            >
              {iv}
            </button>
          ))}
        </div>
      </div>

      <div className="sc-chart-area">
        {!symbol && (
          <div className="sc-overlay">
            <span className="sc-no-data">Select a stock to view chart</span>
          </div>
        )}
        {symbol && loading && (
          <div className="sc-overlay">
            <div className="sc-spinner" />
            <span>Loading candles…</span>
          </div>
        )}
        {symbol && !loading && !hasData && (
          <div className="sc-overlay">
            <span className="sc-no-data">No {chartInterval} candles for this symbol yet</span>
          </div>
        )}
        <div ref={containerRef} className="sc-canvas" />
      </div>
    </div>
  );
}
